import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { LoggerService } from '@klickdata/core/application';
import { BehaviorSubject, EMPTY, Observable, Subscription, combineLatest, fromEvent, merge, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { LiveEditorFieldLockService } from './live-editor-field-lock.service';
import { ResourceService } from './resource.service';

/**
 * Live-editor presence:
 * - After idle timeout → POST .../live-editors/idle
 * - On activity after idle → POST .../live-editors/active
 */
@Injectable({
    providedIn: 'root',
})
export class LiveEditorPresenceTrackerService implements OnDestroy {
    private readonly idleTimeoutMs = 90000;
    private readonly isIdle$ = new BehaviorSubject<boolean>(false);
    private trackingSubscription: Subscription | null = null;
    private notifySubscription: Subscription | null = null;
    private resourceId: number | null = null;
    private hasReportedIdle = false;
    private readonly fieldLock = inject(LiveEditorFieldLockService);

    constructor(
        private ngZone: NgZone,
        private resourceService: ResourceService,
        private logger: LoggerService
    ) {}

    start(resourceId: number): void {
        if (!resourceId) {
            return;
        }
        if (this.resourceId === resourceId && this.trackingSubscription) {
            return;
        }

        this.stop();
        this.resourceId = resourceId;
        this.hasReportedIdle = false;
        this.startIdleTracking();
        this.startPresenceNotifications();
        this.logger.log(`Live editor presence tracking started for resource ${resourceId}`);
    }

    stop(): void {
        this.trackingSubscription?.unsubscribe();
        this.trackingSubscription = null;
        this.notifySubscription?.unsubscribe();
        this.notifySubscription = null;
        this.resourceId = null;
        this.hasReportedIdle = false;
        if (this.isIdle$.getValue()) {
            this.isIdle$.next(false);
        }
    }

    ngOnDestroy(): void {
        this.stop();
    }

    private startPresenceNotifications(): void {
        this.notifySubscription = combineLatest([
            this.isIdle$.pipe(distinctUntilChanged()),
            this.fieldLock.hasOtherEditors$,
        ])
            .pipe(
                filter(() => !!this.resourceId),
                switchMap(([isIdle, hasOtherEditors]) => {
                    const id = this.resourceId;
                    // Alone on the resource there is nobody to report presence to.
                    if (!id || !hasOtherEditors) {
                        return EMPTY;
                    }

                    if (isIdle) {
                        this.hasReportedIdle = true;
                        // Local cleanup / optional PUT of dirty value; server idle clears dominate.
                        this.fieldLock.onIdle();
                        return this.resourceService.notifyLiveEditorIsIdle(id).pipe(
                            tap(() => this.logger.log(`POST live-editors/idle for resource ${id}`)),
                            catchError((err) => {
                                this.logger.log(`POST live-editors/idle failed for resource ${id}`, err);
                                return EMPTY;
                            })
                        );
                    }

                    if (!this.hasReportedIdle) {
                        return EMPTY;
                    }

                    // Always try POST /active after idle (reclaim field + clear idle_editor).
                    return this.fieldLock.resumeActive().pipe(
                        tap((result) => {
                            if (result) {
                                this.logger.log(`POST live-editors/active for resource ${id}`);
                            } else {
                                this.logger.log(
                                    `POST live-editors/active skipped (no field) for resource ${id}`
                                );
                            }
                        }),
                        catchError((err) => {
                            this.logger.log(`POST live-editors/active failed for resource ${id}`, err);
                            return of(null);
                        })
                    );
                })
            )
            .subscribe();
    }

    private startIdleTracking(): void {
        this.ngZone.runOutsideAngular(() => {
            const passiveOptions: AddEventListenerOptions = { passive: true };

            const activityEvents$ = merge(
                this.userActivityFromDocument$(document, passiveOptions),
                fromEvent(window, 'focus'),
                fromEvent(document, 'visibilitychange').pipe(
                    map(() => (document.hidden ? 'hidden' : 'visible')),
                    tap((visibility) => {
                        if (visibility === 'hidden') {
                            this.setIdleState(true, 'tab hidden');
                        }
                    }),
                    map((visibility) => ({ visibility }))
                ),
                this.sameOriginIframeUserActivity$(passiveOptions)
            );

            const blurEvents$ = fromEvent(window, 'blur').pipe(tap(() => this.setIdleState(true, 'window blurred')));

            this.trackingSubscription = merge(activityEvents$, blurEvents$)
                .pipe(
                    startWith({ init: true }),
                    switchMap((evt) => {
                        if (
                            typeof evt === 'object' &&
                            evt !== null &&
                            'visibility' in evt &&
                            (evt as { visibility: string }).visibility === 'hidden'
                        ) {
                            return EMPTY;
                        }

                        this.setIdleState(false, 'activity');

                        return timer(this.idleTimeoutMs).pipe(
                            tap(() => this.setIdleState(true, `no activity for ${this.idleTimeoutMs}ms`))
                        );
                    })
                )
                .subscribe();
        });
    }

    private setIdleState(isIdle: boolean, reason: string): void {
        if (this.isIdle$.getValue() === isIdle) {
            return;
        }

        this.ngZone.run(() => this.isIdle$.next(isIdle));
        this.logger.log(`Live editor presence: ${isIdle ? 'idle' : 'active'} (${reason})`);
    }

    private userActivityFromDocument$(doc: Document, passiveOptions: AddEventListenerOptions): Observable<unknown> {
        return merge(
            fromEvent(doc, 'mousemove'),
            fromEvent(doc, 'mousedown'),
            fromEvent(doc, 'keydown'),
            fromEvent(doc, 'click'),
            fromEvent(doc, 'wheel', passiveOptions),
            fromEvent(doc, 'touchstart', passiveOptions),
            fromEvent(doc, 'touchmove', passiveOptions),
            fromEvent(doc, 'scroll', passiveOptions)
        );
    }

    private sameOriginIframeUserActivity$(passiveOptions: AddEventListenerOptions): Observable<unknown> {
        return new Observable<unknown>((subscriber) => {
            const rootTeardown = new Subscription();
            const subsByIframe = new Map<HTMLIFrameElement, Subscription>();
            const wiredIframes = new WeakSet<HTMLIFrameElement>();

            const hookIframe = (iframe: HTMLIFrameElement) => {
                if (wiredIframes.has(iframe)) {
                    return;
                }
                wiredIframes.add(iframe);

                const onLoad = () => {
                    subsByIframe.get(iframe)?.unsubscribe();
                    try {
                        const idoc = iframe.contentDocument;
                        if (!idoc) {
                            return;
                        }
                        const sub = this.userActivityFromDocument$(idoc, passiveOptions).subscribe((ev) =>
                            subscriber.next(ev)
                        );
                        subsByIframe.set(iframe, sub);
                    } catch {
                        /* cross-origin */
                    }
                };
                iframe.addEventListener('load', onLoad);
                rootTeardown.add(() => iframe.removeEventListener('load', onLoad));
                onLoad();
            };

            document.querySelectorAll('iframe').forEach((el) => {
                if (el instanceof HTMLIFrameElement) {
                    hookIframe(el);
                }
            });

            const mo = new MutationObserver((mutations) => {
                for (const m of mutations) {
                    for (const n of Array.from(m.addedNodes)) {
                        if (n instanceof HTMLIFrameElement) {
                            hookIframe(n);
                        }
                        if (n instanceof Element) {
                            n.querySelectorAll('iframe').forEach((iframeEl) => {
                                if (iframeEl instanceof HTMLIFrameElement) {
                                    hookIframe(iframeEl);
                                }
                            });
                        }
                    }
                }
            });
            mo.observe(document.documentElement, { childList: true, subtree: true });
            rootTeardown.add(() => mo.disconnect());
            rootTeardown.add(() => {
                subsByIframe.forEach((s) => s.unsubscribe());
                subsByIframe.clear();
            });

            return () => {
                rootTeardown.unsubscribe();
            };
        });
    }
}
